import { Box, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getRequestHeaders } from "../../utilities/auth/request";
import SkillsView from "./SkillsView";
import { ACCESS_TOKEN_KEY, USER_ID } from "../../utilities/Constants";

const SkillMappingsEdit = () => {
  const [selectedSkillList, setSelectedSkillList] = useState([]);
  const [skillList, setSkillList] = useState([]);
  const [enableSave, setEnableSave] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleSkillClick = (skill) => {
    const newSkillList = skillList.filter(
      (item) => item.COURSE_ID !== skill.COURSE_ID
    );
    setSkillList(newSkillList);
    setSelectedSkillList([...selectedSkillList, skill]);
    setEnableSave(true);
  };
  const handleDeleteClick = (skill) => {
    const newSkillList = selectedSkillList.filter(
      (item) => item.COURSE_ID !== skill.COURSE_ID
    );
    setSelectedSkillList(newSkillList);
    setSkillList([...skillList, skill]);
    setEnableSave(true);
  };
  const handleOneOfChange = (skill, value) => {
    const newSkillList = selectedSkillList.map((item) => {
      if (item.COURSE_ID === skill.COURSE_ID) {
        return {
          ...item,
          ONE_OF: value,
        };
      }
      return item;
    });
    setSelectedSkillList(newSkillList);
    setEnableSave(true);
  };
  const { enqueueSnackbar } = useSnackbar();

  async function postSkill(data) {
    const response = await fetch("/ui/saveSkillMapping", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ACCESS_TOKEN: JSON.parse(sessionStorage.getItem(ACCESS_TOKEN_KEY))
          .accessToken,
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      enqueueSnackbar("Save Failed", { variant: "error" });
    } else {
      enqueueSnackbar("Saved Successfully", { variant: "success" });
      // Clear the cache for the specific queries
      queryClient.removeQueries(["skills-view"]);
      queryClient.removeQueries(["get-courses", id]);

      // Refetch the queries
      queryClient.refetchQueries(["skills-view"]);
      queryClient.refetchQueries(["get-courses", id]);

      navigate(`/skill-mappings`);
      return response.json();
    }
  }
  const { id } = useParams();
  const { isPending, isError, data, error } = useQuery({
    queryKey: ["skills-view", id],
    queryFn: async () => {
      const request = getRequestHeaders("get", "");
      const response = await fetch(`/ui/fetchSkillMappings`, request);
      if (!response.ok) {
        throw new Error(response.message);
      }
      return response.json();
    },
  });
  const convertToRequiredFormat = (selectedSkillList) => {
    const requiredCourses = [];
    const optionalCourses = {};

    selectedSkillList.forEach((skill) => {
      if (skill.ONE_OF && skill.ONE_OF !== "None") {
        if (!optionalCourses[skill.ONE_OF]) {
          optionalCourses[skill.ONE_OF] = [];
        }
        optionalCourses[skill.ONE_OF].push(skill.COURSE_ID);
      } else {
        requiredCourses.push(skill.COURSE_ID);
      }
    });

    const activities = [];
    data[id].forEach((skill) => {
      if (
        !selectedSkillList.find((item) => item.COURSE_ID === skill.COURSE_ID)
      ) {
        activities.push({
          ACTION: "removed",
          COURSE_CD: skill.COURSE_ID,
          COURSE_NAME: skill.COURSE_NAME,
        });
      }
    });
    selectedSkillList.forEach((skill) => {
      if (!data[id].find((item) => item.COURSE_ID === skill.COURSE_ID)) {
        activities.push({
          ACTION: "added",
          COURSE_CD: skill.COURSE_ID,
          COURSE_NAME: skill.COURSE_NAME,
        });
      }
    });
    console.log(skillList, selectedSkillList);
    const apiData = {
      requiredCourses,
      optionalCourses,
      skill: id,
      updatedBy: sessionStorage.getItem(USER_ID),
      activities,
    };

    return apiData;
  };

  const handleSaveAPI = () => {
    const apiData = convertToRequiredFormat(selectedSkillList);
    postSkill(apiData);
  };

  const {
    isPending: isPendingSkills,
    isError: skillsDataError,
    data: skillsData,
  } = useQuery({
    queryKey: ["get-courses", id],
    queryFn: async () => {
      const request = getRequestHeaders("get", "");
      const response = await fetch(`/ui/getCourses`, request);
      if (!response.ok) {
        throw new Error(response.message);
      }
      return response.json();
    },
  });

  useEffect(() => {
    if (data) {
      setSelectedSkillList(data[id]);
    }
  }, [data, id]);
  useEffect(() => {
    if (skillsData && skillsData.courses) {
      setSkillList(
        skillsData.courses
          .map((item) => ({
            ...item,
            COURSE_ID: item.COURSE_CODE,
          }))
          .filter((item) => {
            return !selectedSkillList.find(
              (skill) => skill.COURSE_ID === item.COURSE_ID
            );
          })
      );
    }
  }, [skillsData, selectedSkillList, setSkillList]);

  if (isPending || isPendingSkills) {
    return <span>Loading...</span>;
  }
  if (isError || skillsDataError) {
    return <span>Error: {error.message}</span>;
  }

  return (
    <>
      <Typography
        variant="h4"
        component={"div"}
        sx={{
          marginBottom: "1rem",
        }}
      >
        Skill Mappings
      </Typography>
      <Box
        sx={{ backgroundColor: "white" }}
        width={"100%"}
        height={"calc(100vh - 128px)"}
        overflow={"auto"}
      >
        <Box display={"flex"} width={"40%"} justifyContent={"space-between"}>
          <Typography
            variant="h6"
            component={"div"}
            sx={{
              p: "1rem",
              fontWeight: "400",
            }}
          >
            Editing - {id}
          </Typography>
          <Box sx={{ mt: 2 }}>{}</Box>
        </Box>
        <Box ml={2}>Available Courses</Box>
        <SkillsView
          selectedSkillData={selectedSkillList}
          skillList={skillList}
          handleDeleteClick={handleDeleteClick}
          handleSkillClick={handleSkillClick}
          enableSave={enableSave}
          oneOffList={skillsData.oneOf}
          handleOneOfChange={handleOneOfChange}
          handleSaveAPI={handleSaveAPI}
        />
      </Box>
    </>
  );
};

export default SkillMappingsEdit;
